import { formatDay, isISODate } from '@mononoke/shared';

/**
 * A NATIVE DATE INPUT THAT READS THE HUMAN WAY — "12 Aug 2026", never the locale's
 * `mm/dd/yyyy` (frames 11a / 15a; the re-audit's biggest visible residual after 0.1.245
 * deliberately left the inputs native).
 *
 * Mechanism: the input is UNTOUCHED — same element, same label wiring, same `.fill()` in
 * the suites, same native picker on every platform — and a pointer-transparent span
 * paints `formatDay(value)` over its text while it holds a valid day and is not focused.
 * Focus (tap, click, keyboard) hides the paint, so editing is exactly the native surface
 * it always was; the native calendar indicator stays outside the painted area, so the
 * affordance to open the picker never disappears.
 *
 * ⚠️ NOT a custom date picker, on purpose. Replacing the input would re-fight iOS/Android
 * picker behaviour the browser already gets right (the 0.1.248 lesson: the device is the
 * instrument) and would break every spec that fills these fields by label.
 *
 * `emptyLabel` covers the one empty state a frame names: the box form's return date shows
 * "From source" (15a) where the native ghost would say `mm/dd/yyyy`. Left off, an empty
 * field keeps its native ghost.
 */
export function DayInput({
  emptyLabel,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { emptyLabel?: string }): JSX.Element {
  const value = typeof props.value === 'string' ? props.value : '';
  const day = isISODate(value) ? formatDay(value) : value === '' ? emptyLabel : undefined;
  return (
    <span className="day-input">
      <input {...props} type="date" />
      {day !== undefined && (
        <span className={value === '' ? 'day-input-display day-input-empty' : 'day-input-display'} aria-hidden="true">
          {day}
        </span>
      )}
    </span>
  );
}
